import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { rendicionAnualService } from '../../../services/api';
import BotonRefresh from './BotonRefresh';

interface LineaReporte {
  codigo: string;
  denominacion: string;
  importe: number;
  nivel: number;
  esSubtotal?: boolean;
  esTotal?: boolean;
}

interface ReportesData {
  rendicionId: string;
  ejercicioNumero: number;
  fechaInicio: string;
  fechaCierre: string;
  partido: {
    denominacion: string;
    sigla: string;
    tipo: string;
    domicilioLegal: string | null;
    localidad: string | null;
  };
  situacionPatrimonial: {
    activo: { lineas: LineaReporte[]; total: number };
    pasivo: { lineas: LineaReporte[]; total: number };
    patrimonioNeto: { lineas: LineaReporte[]; total: number };
    totalPasivoMasPN: number;
    balanceado: boolean;
  };
  recursosYGastos: {
    recursos: { lineas: LineaReporte[]; total: number };
    gastos: { lineas: LineaReporte[]; total: number };
    resultadoEjercicio: number;
  };
  evolucionPN: {
    saldoInicial: number;
    resultadoEjercicio: number;
    saldoFinal: number;
  };
  generadoEn: string;
}

interface Props {
  rendicionId: string;
  onBack: () => void;
  onNext: () => void;
}

type TabKey = 'patrimonial' | 'recursos' | 'evolucion';

function formatImporte(n: number): string {
  return Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(f: string): string {
  return new Date(f).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function Paso5Reportes({ rendicionId, onBack, onNext }: Props) {
  const [tab, setTab] = useState<TabKey>('patrimonial');
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rendicion-reportes', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.getReportes(rendicionId);
      return res.data as ReportesData;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  function refrescarPaso() {
    queryClient.invalidateQueries({ queryKey: ['rendicion-reportes', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-saldos', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-aportes', rendicionId] });
  }

  const headerInfo = useMemo(() => {
    if (!data) return null;
    return {
      titulo: `${data.partido.denominacion} (${data.partido.sigla})`,
      subtitulo: `${data.partido.domicilioLegal ?? ''}${data.partido.localidad ? ' — ' + data.partido.localidad : ''}`,
      periodo: `Ejercicio N° ${data.ejercicioNumero} · ${formatFecha(data.fechaInicio)} al ${formatFecha(data.fechaCierre)}`,
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">
        Generando reportes...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-900">
          No se pudieron generar los reportes: {(error as any)?.response?.data?.error?.message ?? 'error desconocido'}.
        </div>
        <div className="mt-4 flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
          >
            ← Anterior
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end -mb-2">
        <BotonRefresh onClick={refrescarPaso} title="Actualizar paso" />
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          Reportes Generados
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900 mb-4">
          Los siguientes estados se generan <strong>automáticamente</strong> a partir de los saldos
          del Paso 3. Son una vista previa — podés revisarlos antes de generar el reporte final con
          hash en el Paso 6.
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
          {([
            { key: 'patrimonial', label: 'Sit. Patrimonial' },
            { key: 'recursos', label: 'Recursos y Gastos' },
            { key: 'evolucion', label: 'Evolución del PN' },
          ] as { key: TabKey; label: string }[]).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${
                tab === t.key ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Header contable compartido */}
        {headerInfo && (
          <div className="text-center mb-4 pb-3 border-b border-gray-200">
            <div className="font-semibold text-gray-900">{headerInfo.titulo}</div>
            {headerInfo.subtitulo.trim() && (
              <div className="text-xs text-gray-600">{headerInfo.subtitulo}</div>
            )}
            <div className="text-xs text-gray-500">{headerInfo.periodo}</div>
          </div>
        )}

        {/* Contenido de cada tab */}
        {tab === 'patrimonial' && <TabPatrimonial data={data.situacionPatrimonial} />}
        {tab === 'recursos' && <TabRecursosGastos data={data.recursosYGastos} />}
        {tab === 'evolucion' && <TabEvolucionPN data={data.evolucionPN} />}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
        <button
          type="button"
          onClick={onNext}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg"
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Subcomponentes por tab
// =============================================================================

function TablaLineas({ lineas, totalLabel, total }: { lineas: LineaReporte[]; totalLabel: string; total: number }) {
  return (
    <table className="min-w-full text-sm">
      <tbody>
        {lineas.length === 0 ? (
          <tr>
            <td colSpan={2} className="py-3 text-center text-gray-500 italic">
              Sin movimientos.
            </td>
          </tr>
        ) : (
          lineas.map((l, i) => {
            const indentPx = 12 + (l.nivel - 2) * 16;
            return (
              <tr
                key={`${l.codigo}-${i}`}
                className={l.esSubtotal ? 'bg-gray-50 font-semibold' : ''}
              >
                <td
                  className={`py-1 ${l.esSubtotal ? 'text-gray-900' : 'text-gray-700'}`}
                  style={{ paddingLeft: `${indentPx}px` }}
                >
                  <span className="font-mono text-xs text-gray-500 mr-2">{l.codigo}</span>
                  {l.denominacion}
                </td>
                <td
                  className={`py-1 text-right font-mono ${l.esSubtotal ? 'text-gray-900' : 'text-gray-700'}`}
                >
                  {formatImporte(l.importe)}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-gray-800 font-bold">
          <td className="py-2 text-gray-900">{totalLabel}</td>
          <td className="py-2 text-right font-mono text-gray-900">{formatImporte(total)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

function TabPatrimonial({ data }: { data: ReportesData['situacionPatrimonial'] }) {
  return (
    <div className="space-y-6">
      <h3 className="text-center font-semibold text-gray-900 underline">
        ESTADO DE SITUACIÓN PATRIMONIAL
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="font-semibold text-gray-900 mb-2 bg-blue-50 px-2 py-1 rounded">
            ACTIVO
          </div>
          <TablaLineas lineas={data.activo.lineas} totalLabel="TOTAL DEL ACTIVO" total={data.activo.total} />
        </div>

        <div>
          <div className="font-semibold text-gray-900 mb-2 bg-blue-50 px-2 py-1 rounded">
            PASIVO + PATRIMONIO NETO
          </div>
          <TablaLineas lineas={data.pasivo.lineas} totalLabel="Total Pasivo" total={data.pasivo.total} />
          <div className="mt-4">
            <TablaLineas
              lineas={data.patrimonioNeto.lineas}
              totalLabel="Total Patrimonio Neto"
              total={data.patrimonioNeto.total}
            />
          </div>
          <table className="min-w-full text-sm mt-2">
            <tfoot>
              <tr className="border-t-2 border-gray-800 font-bold">
                <td className="py-2 text-gray-900">TOTAL PASIVO + PATRIMONIO NETO</td>
                <td className="py-2 text-right font-mono text-gray-900">
                  {formatImporte(data.totalPasivoMasPN)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div
        className={`text-center p-3 rounded-lg ${
          data.balanceado
            ? 'bg-green-50 text-green-800 border border-green-300'
            : 'bg-red-50 text-red-800 border border-red-300'
        }`}
      >
        {data.balanceado
          ? `✓ Balanceado · Activo = Pasivo + PN = $ ${formatImporte(data.activo.total)}`
          : `✗ Desbalanceado · Activo ($ ${formatImporte(data.activo.total)}) ≠ Pasivo + PN ($ ${formatImporte(data.totalPasivoMasPN)})`}
      </div>
    </div>
  );
}

function TabRecursosGastos({ data }: { data: ReportesData['recursosYGastos'] }) {
  const superavit = data.resultadoEjercicio >= 0;
  return (
    <div className="space-y-6">
      <h3 className="text-center font-semibold text-gray-900 underline">
        ESTADO DE RECURSOS Y GASTOS
      </h3>

      <div>
        <div className="font-semibold text-gray-900 mb-2 bg-blue-50 px-2 py-1 rounded">RECURSOS</div>
        <TablaLineas lineas={data.recursos.lineas} totalLabel="TOTAL RECURSOS" total={data.recursos.total} />
      </div>

      <div>
        <div className="font-semibold text-gray-900 mb-2 bg-blue-50 px-2 py-1 rounded">GASTOS</div>
        <TablaLineas lineas={data.gastos.lineas} totalLabel="TOTAL GASTOS" total={data.gastos.total} />
      </div>

      <div
        className={`text-center p-4 rounded-lg font-bold ${
          superavit ? 'bg-green-50 text-green-800 border border-green-300' : 'bg-red-50 text-red-800 border border-red-300'
        }`}
      >
        {superavit ? 'SUPERÁVIT' : 'DÉFICIT'} DEL EJERCICIO: $ {formatImporte(Math.abs(data.resultadoEjercicio))}
      </div>
    </div>
  );
}

function TabEvolucionPN({ data }: { data: ReportesData['evolucionPN'] }) {
  return (
    <div className="space-y-4">
      <h3 className="text-center font-semibold text-gray-900 underline">
        ESTADO DE EVOLUCIÓN DEL PATRIMONIO NETO
      </h3>

      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="py-2 text-left text-xs font-semibold text-gray-700 uppercase">Concepto</th>
            <th className="py-2 text-right text-xs font-semibold text-gray-700 uppercase w-40">Importe</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="py-2 text-gray-800">Saldo al inicio del ejercicio</td>
            <td className="py-2 text-right font-mono text-gray-800">{formatImporte(data.saldoInicial)}</td>
          </tr>
          <tr>
            <td className="py-2 text-gray-800">
              {data.resultadoEjercicio >= 0 ? 'Superávit' : 'Déficit'} del ejercicio
            </td>
            <td
              className={`py-2 text-right font-mono ${
                data.resultadoEjercicio >= 0 ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {formatImporte(data.resultadoEjercicio)}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-800 font-bold">
            <td className="py-2 text-gray-900">Saldo al cierre del ejercicio</td>
            <td className="py-2 text-right font-mono text-gray-900">{formatImporte(data.saldoFinal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
